import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { SanitizeHTML } from "../SanitizeHtml/SanitizeHtml";
import { ReactComponent as HomeIcon } from "../../assets/icons/home-private.svg";
import { history } from "instantsearch.js/es/lib/routers";
import classes from "./RefinedSearch.module.scss";
import {
  Configure,
  InstantSearch,
  RefinementList,
  SearchBox,
  Hits,
} from "react-instantsearch";
import { useInstantSearch, useRefinementList } from "react-instantsearch";
import { SearchTags } from "../SearchTags/SearchTags";
import { autocompleteSearchSource, searchClient } from "../../helpers/search";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { Results } from "../Results/Results";
import { CountrySelect } from "../CountrySelect/CountrySelect";
import { Button } from "../Button/Button";
import { Hit } from "../Hit/Hit";
import SortBy from "../SortBy/SortBy";
import { RefinedSearchContext } from "../../context";
import { CustomPagination } from "../Pagination/Pagination";
import { ReactComponent as SearchIcon } from "../../assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { LocationState } from "../../@types/search";
import { RefinementListItem } from "instantsearch.js/es/connectors/refinement-list/connectRefinementList";
import {
  IBusinessAreaFields,
  ICountryFields,
  IJobsSearchJobSearchWidgetFields,
  IJobsSearchPostingDateFields,
} from "../../@types/contentful";
import { SharePopUp } from "../SharePopUp/SharePopUp";
import { scrollToStart } from "../../helpers/scrollTo";
import { Checkbox } from "../Checkbox/Checkbox";
import { replaceFilterNames } from "../../helpers/categories";
import { RefinementListProps } from "react-instantsearch";
import { UiState } from "instantsearch.js";
type Props = {
  contentfulData: IJobsSearchJobSearchWidgetFields;
  contentfulJobCategories: IBusinessAreaFields[];
  contentfulCountryList: ICountryFields[];
};

export const RefinedSearch: FC<Props> = ({
  contentfulData,
  contentfulJobCategories,
  contentfulCountryList,
  ...props
}) => {
  const algoliaIndex = process.env.REACT_APP_ALGOLIA_INDICES_JOBS;

  const transformGroups: RefinementListProps["transformItems"] = useCallback(
    (items) => {
      return replaceFilterNames(items, contentfulJobCategories);
    },
    [contentfulJobCategories]
  );

  const transformCountries: RefinementListProps["transformItems"] = useCallback(
    (items) => {
      return replaceFilterNames(items, contentfulCountryList);
    },
    [contentfulCountryList]
  );

  const location = useLocation<LocationState>();
  const sortByValues: { value: string; label: string; id: string }[] = [
    {
      value: process.env.REACT_APP_ALGOLIA_INDICES_JOBS,
      label: contentfulData?.searchResults?.fields?.sortByRelevancy,
      id: "sort-relevant",
    },
    {
      value: process.env.REACT_APP_ALGOLIA_INDICES_BY_DATE,
      label: contentfulData?.searchResults?.fields?.sortByLatest,
      id: "sort-latest",
    },
  ];

  const [numberOfHits, setNumberOfHits] = useState<number | null>(null);
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const autocompleteRef = useRef<any>(null);
  const autocompleteInputRef = useRef<HTMLInputElement>(null);
  const resultsHeaderRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (showFilters) {
      document.body.classList.add("noscroll");
      document
        .getElementById("bp-career-search-widget")
        ?.classList.add("noscroll");
    } else {
      document.body.classList.remove("noscroll");
      document
        .getElementById("bp-career-search-widget")
        ?.classList.remove("noscroll");
    }
  }, [showFilters]);

  useEffect(() => {
    if (location.state?.goBack) {
      setTimeout(() => {
        scrollToStart(resultsHeaderRef);
      }, 100);
    }
  }, [location]);

  const setHitsNumber = (value: number) => {
    setNumberOfHits(value);
  };

  const handleShare = () => {
    setShowPopUp((state) => !state);
  };

  const resetFilters = () => {
    if (autocompleteRef?.current) {
      autocompleteRef.current.setQuery("");
    }
  };
  return (
    <>
    {contentfulJobCategories && contentfulCountryList && contentfulData && (
      <RefinedSearchContext.Provider
        value={{
          setIsFormSubmitted,
          postedDatesSchema: contentfulData?.searchResults?.fields.postingDate
            .fields as IJobsSearchPostingDateFields,
        }}
      >
        <div className={classes.refinedSearch}>
          <InstantSearch
            searchClient={searchClient}
            indexName={algoliaIndex}
            future={{
              preserveSharedStateOnUnmount: false,
            }}
            routing={{
              router: history({
                cleanUrlOnDispose: false,
              }),
              stateMapping: {
                stateToRoute(uiState: UiState) {
                  const indexUiState = uiState[algoliaIndex] || {};
                  const routeInfo: { [key: string]: string | number | string[] } =
                    {};
                  if (indexUiState.query) {
                    routeInfo.query = indexUiState.query as string;
                  }
                  if (indexUiState.page) {
                    routeInfo.page = indexUiState.page;
                  }
                  if (indexUiState.refinementList?.country) {
                    routeInfo.country = indexUiState.refinementList
                      .country as string[];
                  }
                  if (indexUiState.refinementList?.group) {
                    routeInfo.group = indexUiState.refinementList
                      .group as string[];
                  }
                  if (indexUiState.refinementList?.remote) {
                    routeInfo.remote = indexUiState.refinementList
                      .remote as string[];
                  }
                  if (indexUiState.sortBy && sortByValues) {
                    const itemIndex = sortByValues.findIndex(
                      (node) => node.value === indexUiState.sortBy
                    );
                    if (itemIndex !== -1)
                      routeInfo.sortBy = sortByValues[itemIndex].id as string;
                  }
                  return routeInfo as UiState;
                },

                routeToState(routeState: UiState) {
                  const state: {
                    [key: string]: string | number | object | boolean;
                  } = {};
                  if (routeState.page as number) {
                    state.page = routeState.page as number;
                  }
                  if (routeState.query as string) {
                    state.query = routeState.query as string;
                  }
                  if (routeState.sortBy) {
                    const index = sortByValues.findIndex(
                      (node) => node.id === routeState.sortBy
                    );
                    if (index !== -1)
                      state.sortBy = sortByValues[index].value as string;
                  }
                  if (
                    routeState.group ||
                    routeState.country ||
                    routeState.remote
                  ) {
                    state.refinementList = {};
                    if (routeState.group) {
                      state.refinementList = {
                        ...state.refinementList,
                        group: routeState.group as string[],
                      };
                    }
                    if (routeState.country) {
                      state.refinementList = {
                        ...state.refinementList,
                        country: routeState.country as string[],
                      };
                    }
                    if (routeState.remote) {
                      state.refinementList = {
                        ...state.refinementList,
                        remote: routeState.remote as string[],
                      };
                    }
                  }
                  if (location.state?.goBack) {
                    //allows setIsFormSubmitted(true) to be called in <Autocomplete/> on initial load when goBack is true
                    state.open = true;
                  }

                  return {
                    [algoliaIndex]: state,
                  } as UiState;
                },
              },
            }}
          >
            <div ref={resultsHeaderRef} className={classes.refinedSearchHeader}>
              <div className={classes.refinedSearchBars}>
                <div className={classes.refinedSearchCriteria}>
                  <Autocomplete
                    setIsFormSubmitted={setIsFormSubmitted}
                    isFormSubmitted={isFormSubmitted}
                    jobCategories={contentfulJobCategories}
                    getSources={autocompleteSearchSource}
                    inputWrapper="job-search__dropdown"
                    placeholder={contentfulData?.keywordPlaceholder}
                    inputRef={autocompleteInputRef}
                    autocompleteRef={autocompleteRef}
                  />
                  <CountrySelect
                    countryNoResults={contentfulData?.countryNoResults}
                    countryNotSelected={contentfulData?.countryNotSelected}
                    regions={contentfulData?.regions!}
                    countries={contentfulCountryList}
                    placeholder={contentfulData?.countryPlaceholder!}
                    oneCountrySelected={contentfulData?.oneCountrySelected}
                    manyCountriesSelected={contentfulData?.manyCountriesSelected}
                  />
                </div>
                <div className={classes.refinementTags}>
                  <RemoteCheckbox text={contentfulData?.onlyShowRemoteRoles} />
                  <SearchTags
                    tagOptions={contentfulCountryList}
                    showAll={contentfulData?.showAll}
                    showLess={contentfulData?.showLess}
                  />
                </div>
              </div>
              <ButtonBar
                text={contentfulData?.clearAll}
                setIsFormSubmitted={setIsFormSubmitted}
                autocompleteInputRef={autocompleteInputRef}
                autocompleteRef={autocompleteRef}
                reset={resetFilters}
              />
            </div>
            {/* TODO: <ScrollTo /> */}
            {/*query state prop not working without this component, maybe we can make it work without workaround?*/}

            <div style={{ "display": isFormSubmitted ? "flex" : "none" }} className={classes.instasearch}>
              <Configure filters="type: Professionals" hitsPerPage={10} />

              <div className={classes.defaultSearchBox}>
                <SearchBox />
              </div>

              <div
                className={`${classes.refinementsWrapper} ${
                  showFilters ? "show" : ""
                }`}
              >
                <div className={classes.refinementsFilters}>
                  <div>
                    <div className={classes.refinementsHeader}>
                      <div className={classes.refinementsHeaderTitle}>
                        {contentfulData?.searchResults?.fields?.refineFilters}
                      </div>

                      <div
                        onClick={() => setShowFilters(false)}
                        className={`${classes.closeFilters} ${
                          showFilters ? "show" : "hide"
                        }`}
                      >
                        <CloseIcon />
                      </div>
                    </div>
                    <RefinementList attribute="remote" />
                    <div>
                      <div className={classes.filters}>
                        {contentfulData?.searchResults?.fields?.sortBy}
                      </div>
                      <div className={classes.sorting}>
                        <SortBy items={sortByValues} />
                      </div>
                    </div>
                    <div>
                      <div className={classes.filters}>
                        {contentfulData?.searchResults?.fields?.jobCategory}
                      </div>
                      <RefinementList
                        showMore
                        limit={5}
                        className={classes.refinements}
                        attribute="group"
                        transformItems={transformGroups}
                        translations={{
                          showMoreButtonText({ isShowingMore }) {
                            return isShowingMore
                              ? contentfulData?.searchResults?.fields?.showLess
                              : contentfulData?.searchResults.fields?.showMore;
                          },
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className={classes.filters}>
                      {contentfulData?.country}
                    </div>
                    <RefinementList
                      showMore
                      limit={6}
                      attribute="country"
                      className={classes.refinements}
                      transformItems={transformCountries}
                      translations={{
                        showMoreButtonText({ isShowingMore }) {
                          return isShowingMore
                            ? contentfulData?.searchResults?.fields?.showLess
                            : contentfulData?.searchResults.fields?.showMore;
                        },
                      }}
                    />
                  </div>
                  <Button
                    rounded
                    className={classes.applyButton}
                    onClick={() => setShowFilters(false)}
                    variant="transparent"
                  >
                    {contentfulData?.searchResults?.fields?.applyButton}
                  </Button>
                  <SecondBar
                    text={contentfulData?.searchResults?.fields.resetFilters}
                    autocompleteRef={autocompleteRef}
                    reset={resetFilters}
                  />
                </div>
              </div>
              <div className={classes.result}>
                {!numberOfHits && (
                  <div className={classes.noHits}>
                    {contentfulData?.searchResults?.fields?.noResults}
                  </div>
                )}
                {numberOfHits !== null && numberOfHits > 0 && (
                  <div className={classes.resultTitle}>
                    {contentfulData?.searchResults?.fields.jobsFound.replace(
                      "{n}",
                      `${numberOfHits ? numberOfHits : ""}`
                    )}
                    <button
                      onClick={() => setShowFilters(true)}
                      className={classes.resultHeaderRefine}
                    >
                      {contentfulData?.searchResults?.fields.refineFilters}
                    </button>
                  </div>
                )}
                <div className={classes.resultHeader}>
                  <button className={classes.resultCopy} onClick={handleShare}>
                    <SanitizeHTML
                      html={
                        contentfulData?.searchResults?.fields.copySearchUrl
                          .replace("{", "<span>")
                          .replace("}", "</span>") as string
                      }
                    />
                  </button>
                  {showPopUp && (
                    <SharePopUp
                      className={classes.sharePopUp}
                      title={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.title as string
                      }
                      emailSubject={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.emailSubject as string
                      }
                      emailShare={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.emailShare as string
                      }
                      jobLink={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.jobLink as string
                      }
                      linkCopy={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.linkCopy as string
                      }
                      doneButton={
                        contentfulData.searchResults?.fields.sharePopUp.fields
                          ?.doneButton as string
                      }
                      setShowPopUp={setShowPopUp}
                    />
                  )}
                </div>
                <Results setNumber={setHitsNumber}>
                  <Hits hitComponent={Hit} />
                </Results>
                <CustomPagination />
              </div>
            </div>
          </InstantSearch>
        </div>
      </RefinedSearchContext.Provider>
    )}
    </>
  );
};

function SecondBar({ text, reset }: any) {
  const { setIndexUiState } = useInstantSearch();
  const resetFilters = () => {
    reset();
    setIndexUiState({});
  };
  return (
    <div className={classes.refinementsResetContainer}>
      <button onClick={resetFilters} className={classes.refinementsReset}>
        <span>{text}</span>
      </button>
    </div>
  );
}

function RemoteCheckbox({ text, ...props }: any) {
  const { indexUiState } = useInstantSearch();
  const { refine } = useRefinementList(
    {
      attribute: "remote",
    },
    {
      $$widgetType: "ais.refinementList",
    }
  );

  const [isChecked, setIsChecked] = useState<boolean>(
    indexUiState?.refinementList?.remote?.includes("true") || false
  );

  const handleToggleRemote = () => {
    setIsChecked(!isChecked);
    refine("true");
  };
  return (
    <div className={classes.refinedRemote} onClick={handleToggleRemote}>
      <div className={classes.refinedRemote + " " + classes.smallGap}>
        <HomeIcon />
        {text}
      </div>
      <Checkbox isSelected={isChecked} />
    </div>
  );
}

function ButtonBar({
  text,
  setIsFormSubmitted,
  autocompleteRef,
  autocompleteInputRef,
  reset,
  ...props
}: any) {
  const { indexUiState, setIndexUiState } = useInstantSearch();
  const resetFilters = () => {
    reset();
    setIndexUiState({});
  };
  const handleButtonSearch = () => {
    setIsFormSubmitted(true);
    console.log("handle button search", { indexUiState });

    if (autocompleteInputRef.current?.value) {
      setIsFormSubmitted(true);
    } else {
      console.log("handle button search", { indexUiState });
    }
  };

  return (
    <div className={classes.refinedSearchButtons}>
      <Button
        className={classes.refinementSubmitButton}
        onClick={handleButtonSearch}
      >
        <SearchIcon />
      </Button>
      <Button
        onClick={() => resetFilters()}
        variant={"textOnly"}
        className={classes.refinementSecondRowButton}
      >
        {text}
      </Button>
    </div>
  );
}
